export interface LabelingExperiment {
  experimentId: string;
  toolIds: string[];
}

/** Wear verdict of a tool — what the CNN predicts and what the dichotomous search brackets. */
export type ToolState = 'ok' | 'worn';

/**
 * Everything an annotator can assign to an image: a wear verdict, or `unreadable` when the image
 * cannot be judged at all (blurred, obstructed, badly lit…). Unreadable images are labeled — they
 * count as done — but carry no information about the tool's wear.
 */
export type ImageLabel = ToolState | 'unreadable';

export const IMAGE_LABELS: readonly ImageLabel[] = ['ok', 'worn', 'unreadable'];

/** Narrows a raw backend label (a free string server-side) to the values the UI understands. */
export function toImageLabel(raw: string | null | undefined): ImageLabel | null {
  return IMAGE_LABELS.includes(raw as ImageLabel) ? (raw as ImageLabel) : null;
}

export type CnnLabel = 'good' | 'worn';

export interface LabelingSession {
  sessionId: string;
  experimentId: string;
  name: string;
  description: string | null;
  companyName: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  labelCount: number;
}

export interface LabelingSessionImage {
  imageKey: string;
  relativePath: string;
  filename: string;
  experimentId: string;
  partId: string | null;
  operationId: string | null;
  stackId: string | null;
  createdAt: string | null;
  label: string | null;
  /** Presigned RustFS URL — expires, refresh via labelizeGetSignedImageUrl. */
  url: string;
  machineName: string | null;
}

/** One stored label, as returned with a session's detail. */
export interface LabelingSessionLabel {
  imageKey: string;
  /** Raw server string — narrow with `toImageLabel` before trusting it. */
  label: string;
}

/**
 * Every annotator's labels for one experiment (read-only export route). Sessions without a label
 * are left out server-side; `images` is listed once and is empty when no session holds a label.
 */
export interface LabelingExperimentLabels {
  experimentId: string;
  sessions: Array<Omit<LabelingSession, 'labelCount'> & { labels: LabelingSessionLabel[] }>;
  images: Array<Omit<LabelingSessionImage, 'label'>>;
}

export interface CnnSuggestion {
  label: CnnLabel;
  mappedState: ToolState;
  confidence?: number;
  at: string;
}

export interface ToolImage {
  key: string;
  /** Raw source ids, kept so a presigned URL can be re-signed on load failure. */
  experimentId: string;
  partId: string | null;
  operationId: string | null;
  toolId: string;
  ref: string;
  machine: string;
  magnification: number;
  edge: number;
  capturedAt: string;
  imageUrl: string;
  label: ImageLabel | null;
  cnn: CnnSuggestion | null;
}

export interface HistoryEntry {
  key: string;
  label: ImageLabel | null;
}

export interface LabelingState {
  items: ToolImage[];
  currentKey: string;
  mode: 'focus' | 'grid' | 'dichotomous';
  statusFilter: 'all' | 'todo' | 'done';
  seriesFilter: string;
  selected: Set<string>;
  autoAdvance: boolean;
  history: HistoryEntry[];
}

export interface ClassifyResult {
  key: string;
  label: CnnLabel;
  confidence?: number;
}
